/**
 * Singleton in-memory store for parsed file data.
 * Files are stored per-session and never leave the browser.
 */

export interface ParsedSheet {
  name: string;
  headers: string[];
  rows: Array<Record<string, unknown>>;
  totalRows: number;
  totalColumns: number;
}

export type ParsedFileType = 'spreadsheet' | 'image' | 'text';

export interface ParsedFileData {
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  type: ParsedFileType;
  /** Spreadsheet-only fields */
  sheets: ParsedSheet[];
  totalCells: number;
  /** Text file content (for type === 'text') */
  textContent?: string;
  /** Base64 data URI (for type === 'image', includes data:mime;base64, prefix) */
  base64Data?: string;
  /** Original File object — needed for FormData upload to the workspace endpoint. */
  rawFile?: File;
  parsedAt: number;
}

class FileStore {
  private files = new Map<string, ParsedFileData>();

  add(data: ParsedFileData): void {
    this.files.set(data.fileId, data);
  }

  get(fileId: string): ParsedFileData | undefined {
    return this.files.get(fileId);
  }

  getAll(): ParsedFileData[] {
    return Array.from(this.files.values());
  }

  remove(fileId: string): boolean {
    return this.files.delete(fileId);
  }

  clear(): void {
    this.files.clear();
  }

  get size(): number {
    return this.files.size;
  }

  /**
   * Find a file by name (case-insensitive).
   * Useful when the LLM references a file by name in a query.
   */
  findByName(fileName: string): ParsedFileData | undefined {
    const lower = fileName.toLowerCase();
    for (const file of this.files.values()) {
      if (file.fileName.toLowerCase() === lower) return file;
    }
    return undefined;
  }
}

/** Global singleton — shared across the entire frontend session. */
export const fileStore = new FileStore();
